// Smart Objects panel — poster-project parametric recipes (Text FX / Charts /
// Math / Decor). LIST MODE ONLY (Smart Tools epic, 2026-07-23): category
// ACCORDIONS over the recipe catalog; each open category is a GRID of TILES
// showing an ACTUAL rendered thumbnail of the smart object
// (smartObjectPreview.js generates the recipe at its defaults and renders it
// to a transparent PNG — the same "same treatment" the Text panel got).
// Thumbs render lazily per open category. Click inserts via
// editor.addSmartObject (one undo entry, centered).
//
// The old EDIT MODE (schema-driven param form in this drawer) is RETIRED —
// editing now opens the SmartToolModal (owner directive: "options presented
// in a perfectly styled modal instead of ... the left hand drawer"). When the
// selection is a smart object, this panel shows only a banner that opens that
// modal (openSmartToolModal — smartToolModalController). The field-control
// machinery (SmartObjectFieldControl/ListField/FillField + the draft helpers
// in SmartObjectFieldUtils) lives on inside the modal.
//
// Shell classes (cce-tool-sidebar/-header) come from StudioCoreEditor.css;
// panel-specific styles live in SmartObjectsSidebar.css.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { listRecipes } from '../../../posterTools';
import { ensureFont } from './fonts/fontLoader';
import { renderSmartObjectThumb } from './smartObjectPreview';
import { openSmartToolModal } from './smartToolModalController';
import { openSmartToolsBrowser } from './smartToolsBrowserController';
import { browsableRecipes, featuredRecipes } from './smartToolsTaxonomy';
import { collectDraftFontFamilies } from './SmartObjectFieldUtils';
import './SmartObjectsSidebar.css';

// The drawer is the QUICK surface (a Browse-all button + a small Featured grid,
// SM-B 2026-07-23); the Smart Tools Browser owns full categorized navigation
// (220-tool-ready). The old category accordions retired with it. 'text' recipes
// (art-text) never surface here — they live under the Text panel's "Text
// Effects" (browsableRecipes drops them).

// Device px for the tile thumbnails — rendered once, downscaled crisp into the
// ~76px art box. Bigger than the Text panel's 72px swatch because smart-object
// art (charts/tables/fractions) carries fine lines that need the extra detail.
const SO_THUMB_PX = 128;

export default function SmartObjectsSidebar({ editor, isOpen, onClose }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Rendered thumbnails for the FEATURED tiles: recipeId → blob URL (LRU-owned
  // by smartObjectPreview).
  const [thumbs, setThumbs] = useState({});
  // recipeIds we've already kicked a render for — dedupe across re-renders.
  const requestedThumbs = useRef(new Set());

  // Selection mirror for the edit banner only — the actual editing surface is
  // the SmartToolModal (edit mode reads the selection itself when opened).
  const selection = editor?.getSelectedSmartObject?.() || null;
  const recipe = selection?.recipe || null;

  // The slimmed drawer's Featured grid (≤6): featured:true recipes, padded with
  // the first tool of each remaining domain so it's never sparse (see
  // smartToolsTaxonomy). Full categorized navigation lives in the Browser.
  const featured = useMemo(() => featuredRecipes(listRecipes(), 6), []);
  const totalCount = useMemo(() => browsableRecipes(listRecipes()).length, []);

  // Eagerly render the ≤6 Featured thumbnails when the drawer opens —
  // smartObjectPreview serializes + LRU-caches and requestedThumbs dedupes, so
  // it stays cheap. A thumb that fails (null) simply keeps its placeholder. The
  // Browser drives the 220-tool grid with its own IntersectionObserver.
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    featured.forEach((r) => {
      if (requestedThumbs.current.has(r.id)) return;
      requestedThumbs.current.add(r.id);
      renderSmartObjectThumb({ recipeId: r.id, size: SO_THUMB_PX })
        .then((url) => { if (alive && url) setThumbs((m) => ({ ...m, [r.id]: url })); })
        .catch(() => { /* tile keeps its neutral placeholder */ });
    });
    return () => { alive = false; };
  }, [isOpen, featured]);

  if (!isOpen) return null;

  const handleInsert = async (r) => {
    if (!editor || busy) return;
    setBusy(true);
    try {
      // Register the recipe's DEFAULT fonts before it generates — same reason
      // Apply does it below. Insert passes no params, so the recipe runs on
      // its defaults; a default that names a catalog (non-bundled) family is
      // not in document.fonts yet, and canvas text doesn't trigger font
      // loading by itself, so the recipe would measure and rasterize the
      // fallback serif. Recipes that measure font metrics to place geometry
      // (handwriting-lines rules its midline at the font's x-height) would
      // then bake the WRONG face's metrics into the object.
      // Reading the defaults through the schema keeps this correct for every
      // recipe with a `font` field, not just the handwriting sheet.
      const families = collectDraftFontFamilies(r.schema || [], r.defaults || {});
      if (families.length > 0) {
        // Soft-fail like the rest of the font system: a family that can't
        // load must not block the insert (canvas falls back).
        await Promise.allSettled(families.map((f) => ensureFont(f)));
      }
      await editor.addSmartObject(r.id);
    } catch (err) {
      console.error('Smart object insert failed:', err);
      toast.showError(`Could not add "${r.name}". ${err.message || 'Please try again.'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>Smart Objects</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-so-list">
          {/* The one edit affordance left in the drawer: opens the Smart Tool
              modal for the selected object (edit mode reads the selection). */}
          {selection && recipe && (
            <button
              type="button"
              className="cce-so-edit-banner"
              onClick={() => openSmartToolModal({ mode: 'edit' })}
            >
              ✎ Edit selected: {recipe.name}
            </button>
          )}

          {/* Prominent entry to the full categorized catalog (SM-B Browser). */}
          <button
            type="button"
            className="cce-so-browse-all"
            onClick={() => openSmartToolsBrowser({ editor })}
          >
            <span className="cce-so-browse-icon" aria-hidden="true">▦</span>
            <span className="cce-so-browse-text">
              <span className="cce-so-browse-title">Browse all smart tools</span>
              <span className="cce-so-browse-sub">
                Search {totalCount} tools by subject, tag &amp; more
              </span>
            </span>
            <span className="cce-so-browse-arrow" aria-hidden="true">→</span>
          </button>

          <div className="cce-so-featured-head">Featured</div>
          {featured.length > 0 ? (
            <div className="cce-so-grid">
              {featured.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="cce-so-tile"
                  disabled={busy}
                  onClick={() => handleInsert(r)}
                  title={r.description}
                >
                  <span className="cce-so-tile-art">
                    {thumbs[r.id]
                      ? <img className="cce-so-tile-img" src={thumbs[r.id]} alt="" draggable={false} />
                      : <span className="cce-so-tile-ph" aria-hidden="true">▦</span>}
                  </span>
                  <span className="cce-so-tile-label">{r.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="cce-so-empty">No smart object recipes registered.</div>
          )}

          <div className="cce-so-tips">
            <p>Click a featured tool to add it — or “Browse all” for the full catalog.</p>
            <p>Select a smart object and press “✎ Edit” (here or in the toolbar) — or double-click it — to open its settings.</p>
          </div>
      </div>
    </div>
  );
}
